import Debounce from '@sd-utility/debounce';
import { namedUuid } from '@tvg/fuse-utils';
import noScroll from 'no-scroll';
import PropTypes from 'prop-types';
import React from 'react';
import { Button } from '../../fuse-buttons/src/buttons';
import DrawerHeader from './drawer-header';
import {
	Content,
	Dialog,
	DrawerBody,
	DrawerBodyContent,
	DrawerPromotionalArea,
	StyledStickyDrawer,
} from './styled-drawer-dialogue';

const { forwardRef, useCallback, useEffect, useMemo, useState, useRef } = React;
const sidesEnum = Object.freeze({
	left: 'left',
	right: 'right',
	top: 'top',
	bottom: 'bottom',
});

const propTypes = {
	children: PropTypes.node.isRequired,
	title: PropTypes.string.isRequired,
	backCallback: PropTypes.func,
	closeCallback: PropTypes.func,
	dataTestId: PropTypes.string,
	hasBackButton: PropTypes.bool,
	promotionalArea: PropTypes.node,
	onClose: PropTypes.func,
	open: PropTypes.bool,
	stickyDrawer: PropTypes.shape({
		container: PropTypes.shape({
			className: PropTypes.string,
			dataTestId: PropTypes.string,
			open: PropTypes.bool,
			transition: PropTypes.string,
			zIndex: PropTypes.string,
		}),
		button: PropTypes.element,
	}),
	slidePosition: PropTypes.oneOf([
		sidesEnum.bottom,
		sidesEnum.top,
		sidesEnum.left,
		sidesEnum.right,
	]),
	srLabelBackButton: PropTypes.string,
	srLabelCloseButton: PropTypes.string,
	zIndex: PropTypes.string,
	componentId: PropTypes.string,
};

const defaultProps = {
	backCallback: () => {},
	closeCallback: () => {},
	dataTestId: undefined,
	hasBackButton: false,
	promotionalArea: undefined,
	onClose: () => {},
	open: false,
	stickyDrawer: {
		container: {
			className: 'fuse-drawer__sticky-drawer',
			dataTestId: 'fuse-drawer-sticky-drawer-data-testid',
			open: false,
			transition: '200ms',
			zIndex: '1',
		},
		button: <Button />,
	},
	slidePosition: sidesEnum.right,
	srLabelBackButton: undefined,
	srLabelCloseButton: undefined,
	zIndex: '1',
	componentId: namedUuid('fuse-drawer-header'),
};

const DrawerDialog = forwardRef(
	(
		{
			backCallback,
			children,
			closeCallback,
			dataTestId,
			hasBackButton,
			promotionalArea,
			onClose,
			open,
			stickyDrawer,
			slidePosition,
			srLabelBackButton,
			srLabelCloseButton,
			title,
			zIndex,
		},
		ref,
	) => {
		// States
		const [isScrolled, setIsScrolled] = useState(false);
		const [isFocusInside, setIsFocusInside] = useState(false);
		// Refs
		const drawerContentRef = useRef();
		// Ids
		const drawerHeadingId = useMemo(() => namedUuid('fuse-drawer-header'), []);
		// StickyDrawer config
		const { container: stickyDrawerProps } = stickyDrawer;
		const isStickyDrawerOpen = stickyDrawerProps.open;
		// Others
		const stickyDrawerHeight = isStickyDrawerOpen
			? drawerContentRef?.current?.children[2]?.offsetHeight
			: 0;
		// Event handlers
		const handleScroll = useCallback(
			(e) =>
				Debounce((container) => {
					const { scrollTop } = container;
					const isCurrentlyScrolled = scrollTop > 0;

					setIsScrolled(isCurrentlyScrolled);
				}, 20)(e.currentTarget),
			[isScrolled],
		);

		// Focus on container only once
		const handleBlur = useCallback(() => {
			if (open && !isFocusInside) setIsFocusInside(true);
		}, [open, isFocusInside, setIsFocusInside]);

		useEffect(() => {
			if (!open) setIsFocusInside(false);
		}, [open, setIsFocusInside]);

		// Set noScroll and activeTrap only when modal opens and cleanup on close
		useEffect(() => {
			if (open) {
				noScroll.on();
			} else {
				noScroll.off();
			}

			return function cleanup() {
				if (!open) noScroll.off();
			};
		}, [open]);

		return (
			<Dialog
				aria-hidden={open ? undefined : true}
				aria-labelledby={drawerHeadingId}
				aria-modal={open}
				data-testid={dataTestId}
				inert={open ? undefined : 'true'}
				open={open}
				role="dialog"
				ref={ref}
				slidePosition={slidePosition}
				tabIndex={open && !isFocusInside ? '0' : '-1'}
				zIndex={zIndex}
				onBlur={handleBlur}
			>
				<Content open={open} ref={drawerContentRef}>
					<DrawerHeader
						title={title}
						backCallback={(event) => {
							backCallback(event, ref);
						}}
						closeCallback={(event) => {
							closeCallback(event, ref);
							onClose();
						}}
						drawerHeadingId={drawerHeadingId}
						hasBackButton={hasBackButton}
						hasDepth={isScrolled}
						srLabelBackButton={srLabelBackButton}
						srLabelCloseButton={srLabelCloseButton}
					/>
					<DrawerBody
						hasPromotionalArea={promotionalArea}
						onScroll={handleScroll}
						stickyDrawerHeight={stickyDrawerHeight}
					>
						{promotionalArea && <DrawerPromotionalArea>{promotionalArea}</DrawerPromotionalArea>}
						{children && (
							<DrawerBodyContent hasPromotionalArea={promotionalArea}>{children}</DrawerBodyContent>
						)}
					</DrawerBody>
					<StyledStickyDrawer {...stickyDrawerProps}>{stickyDrawer.button}</StyledStickyDrawer>
				</Content>
			</Dialog>
		);
	},
);

DrawerDialog.propTypes = propTypes;
DrawerDialog.defaultProps = defaultProps;

export default DrawerDialog;
